import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import theme from './theme/theme';
import Layout from './components/Layout';
import HomePage from './components/HomePage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';
import Dashboard from './components/dashboard/Dashboard';
import Clients from './components/clients/Clients';
import ClientHistory from './components/clients/ClientHistory';
import Appointments from './components/appointments/Appointments';
import Services from './components/services/Services';
import Products from './components/products/Products';
import MembershipManagement from './components/memberships/MembershipManagement';
import ClientMemberships from './components/memberships/ClientMemberships';
import BrandManagement from './components/brands/BrandManagement';
import Invoices from './components/invoices/Invoices';
import InvoiceForm from './components/invoices/InvoiceForm';
import InvoiceEdit from './components/invoices/InvoiceEdit';
import InvoiceView from './components/invoices/InvoiceView';
import Reports from './components/reports/Reports';
import SalesReport from './components/reports/SalesReport';
import SalesByStaff from './components/reports/SalesByStaff';
import SalesByServiceAndProducts from './components/reports/SalesByServiceAndProducts';
import Expenses from './components/expenses/Expenses';
import Profile from './components/profile/Profile';
import Settings from './components/settings/Settings';
import RoleManagement from './components/roles/RoleManagement';
import RoleForm from './components/roles/RoleForm';
import UserManagement from './components/admin/UserManagement';
import SuperAdminDashboard from './components/superadmin/SuperAdminDashboard';
import TenantManagement from './components/superadmin/TenantManagement';
import TenantServicesUpload from './components/superadmin/TenantServicesUpload';
import EmailTemplateManagement from './components/superadmin/EmailTemplateManagement';
import EmailTemplateForm from './components/superadmin/EmailTemplateForm';
import EmailTest from './components/test/EmailTest';
import EmailConfirmation from './components/auth/EmailConfirmation';
import EmailVerification from './components/auth/EmailVerification';
import Subscriptions from './components/admin/Subscriptions';
import Invoice from './components/admin/Invoice';
import Followups from './components/followups/Followups';

// Subscription Management Components
import SubscriptionPlansManagement from './components/superadmin/SubscriptionPlansManagement';
import SubscriptionPlanForm from './components/superadmin/SubscriptionPlanForm';
import SubscriptionFeaturesManagement from './components/superadmin/SubscriptionFeaturesManagement';
import SubscriptionFeatureForm from './components/superadmin/SubscriptionFeatureForm';
import TenantSubscriptionsManagement from './components/superadmin/TenantSubscriptionsManagement';
import TenantSubscriptionRenewal from './components/superadmin/TenantSubscriptionRenewal';
import SuperAdminInvoice from './components/superadmin/SuperAdminInvoice';
import SubscriptionAnalytics from './components/superadmin/SubscriptionAnalytics';
import authService from './services/auth';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import './components/invoices/invoice-print.css';

function App() {
  // Global event handlers for number inputs
  useEffect(() => {
    const handleNumberInputWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleNumberInputKeyDown = (e) => {
      if (e.target.type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
      }
    };

    // Add global event listeners
    document.addEventListener('wheel', handleNumberInputWheel, { passive: false });
    document.addEventListener('keydown', handleNumberInputKeyDown);

    // Cleanup event listeners on unmount
    return () => {
      document.removeEventListener('wheel', handleNumberInputWheel);
      document.removeEventListener('keydown', handleNumberInputKeyDown);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CurrencyProvider>
        <TimezoneProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/email-confirmation" element={<EmailConfirmation />} />
              <Route path="/verify-email" element={<EmailVerification />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="clients" element={<ProtectedRoute requiredPermission="clients.view"><Clients /></ProtectedRoute>} />
                <Route path="clients/:id/history" element={<ProtectedRoute requiredPermission="clients.view"><ClientHistory /></ProtectedRoute>} />
                <Route path="appointments" element={<ProtectedRoute requiredPermission="appointments.view"><Appointments /></ProtectedRoute>} />
                <Route path="services" element={<ProtectedRoute requiredPermission="services.view"><Services /></ProtectedRoute>} />
                <Route path="products" element={<ProtectedRoute requiredPermission="products.view"><Products /></ProtectedRoute>} />
                <Route path="memberships" element={<ProtectedRoute requiredPermission="memberships.view"><MembershipManagement /></ProtectedRoute>} />
                <Route path="memberships/clients" element={<ProtectedRoute requiredPermission="clients.view"><ClientMemberships /></ProtectedRoute>} />
                <Route path="brands" element={<ProtectedRoute requiredPermission="brands.view"><BrandManagement /></ProtectedRoute>} />
                <Route path="invoices" element={<ProtectedRoute requiredPermission="invoices.view"><Invoices /></ProtectedRoute>} />
                <Route path="invoices/view/:id" element={<ProtectedRoute requiredPermission="invoices.view"><InvoiceView /></ProtectedRoute>} />
                <Route path="invoices/new" element={<ProtectedRoute requiredPermission="invoices.add"><InvoiceForm /></ProtectedRoute>} />
                <Route path="invoices/edit/:id" element={<ProtectedRoute requiredPermission="invoices.edit"><InvoiceEdit /></ProtectedRoute>} />
                <Route path="reports" element={<ProtectedRoute requiredPermission="reports.view"><Reports /></ProtectedRoute>} />
                <Route path="reports/sales" element={<ProtectedRoute requiredPermission="reports.view"><SalesReport /></ProtectedRoute>} />
                <Route path="reports/staff" element={<ProtectedRoute requiredPermission="reports.view"><SalesByStaff /></ProtectedRoute>} />
                <Route path="reports/service-products" element={<ProtectedRoute requiredPermission="reports.view"><SalesByServiceAndProducts /></ProtectedRoute>} />
                <Route path="expenses" element={<ProtectedRoute requiredPermission="expenses.view"><Expenses /></ProtectedRoute>} />
                <Route path="roles" element={<ProtectedRoute requiredPermission="roles.view"><RoleManagement /></ProtectedRoute>} />
                <Route path="roles/new" element={<ProtectedRoute requiredPermission="roles.add"><RoleForm /></ProtectedRoute>} />
                <Route path="roles/edit/:id" element={<ProtectedRoute requiredPermission="roles.edit"><RoleForm /></ProtectedRoute>} />
                <Route path="users" element={<ProtectedRoute adminOnly redirectTo="/dashboard"><UserManagement /></ProtectedRoute>} />
                <Route path="admin/roles" element={<ProtectedRoute requiredPermission="roles.view"><RoleManagement /></ProtectedRoute>} />
                <Route path="admin/users" element={<ProtectedRoute adminOnly redirectTo="/dashboard"><UserManagement /></ProtectedRoute>} />
                <Route path="profile" element={<Profile />} />
                <Route path="change-password" element={<ChangePassword />} />
                <Route path="settings" element={<ProtectedRoute requiredPermission="settings.view"><Settings /></ProtectedRoute>} />
                <Route path="subscriptions" element={<ProtectedRoute requiredPermission="subscriptions.view"><Subscriptions /></ProtectedRoute>} />
                <Route path="invoice/:id" element={<ProtectedRoute requiredPermission="subscriptions.view"><Invoice /></ProtectedRoute>} />
                <Route path="follow-ups" element={<ProtectedRoute requiredPermission="clients.view"><Followups /></ProtectedRoute>} />
                <Route path="test-email" element={<EmailTest />} />
                {/* Prevent access to removed/unknown dashboard subroutes (e.g. /dashboard/admin) */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />

              </Route>

              {/* Subscription Invoices - Separate namespace from Sales invoices */}
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="invoices" element={<ProtectedRoute requiredPermission="subscriptions.view"><Subscriptions /></ProtectedRoute>} />
                <Route path="invoices/:id" element={<ProtectedRoute requiredPermission="subscriptions.view"><Invoice /></ProtectedRoute>} />
              </Route>

              {/* Super Admin Routes - Separate from dashboard */}
              <Route
                path="/superadmin"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<SuperAdminDashboard />} />
                <Route path="tenants" element={<TenantManagement />} />
                <Route path="tenants/:tenantId/upload-services" element={<TenantServicesUpload />} />
                <Route path="email-templates" element={<EmailTemplateManagement />} />
                <Route path="email-templates/new" element={<EmailTemplateForm />} />
                <Route path="email-templates/edit/:id" element={<EmailTemplateForm />} />

                {/* Subscription Management Routes */}
                <Route path="subscription-plans" element={<SubscriptionPlansManagement />} />
                <Route path="subscription-plan/new" element={<SubscriptionPlanForm />} />
                <Route path="subscription-plan/:id" element={<SubscriptionPlanForm />} />
                <Route path="subscription-features" element={<SubscriptionFeaturesManagement />} />
                <Route path="subscription-features/new" element={<SubscriptionFeatureForm />} />
                <Route path="subscription-features/edit/:id" element={<SubscriptionFeatureForm />} />
                <Route path="tenant-subscriptions" element={<TenantSubscriptionsManagement />} />
                <Route path="tenant-subscriptions/renew" element={<TenantSubscriptionRenewal />} />
                <Route path="tenant-subscriptions/invoice/:id" element={<SuperAdminInvoice />} />
                <Route path="subscription-analytics" element={<SubscriptionAnalytics />} />
              </Route>

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </TimezoneProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
}

export default App;